import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

export default function AdminAliases() {
  const [type, setType] = useState("college");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ raw: "", canonical: "" });

  const load = async (t = type) => {
    try {
      const { data } = await api.get("/admin/aliases", { params: { entity_type: t } });
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    load(type);
  }, [type]);

  const save = async () => {
    if (!form.raw || !form.canonical) return toast.error("Both raw and canonical names are required");
    try {
      await api.post("/admin/aliases", { entity_type: type, ...form });
      toast.success("Alias saved");
      setForm({ raw: "", canonical: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (raw) => {
    await api.delete("/admin/aliases", { params: { entity_type: type, raw } });
    toast.success("Alias deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-aliases-page">
      <PageHead
        title="Alias mapping"
        subtitle="Historical names stay in the original record; aliases point them to one canonical college or branch entity."
      />

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-white p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Entity</span>
          <select
            data-testid="alias-type-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 border border-slate-300 px-2 text-sm"
          >
            <option value="college">College</option>
            <option value="branch">Branch</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Raw name in file</span>
          <input
            data-testid="alias-raw-input"
            value={form.raw}
            onChange={(e) => setForm((p) => ({ ...p, raw: e.target.value }))}
            placeholder="B.M.C.R.I, Bengaluru"
            className="h-9 w-72 border border-slate-300 px-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Canonical name</span>
          <input
            data-testid="alias-canonical-input"
            value={form.canonical}
            onChange={(e) => setForm((p) => ({ ...p, canonical: e.target.value }))}
            placeholder="Bangalore Medical College and Research Institute"
            className="h-9 w-80 border border-slate-300 px-2 text-sm"
          />
        </label>
        <button
          data-testid="alias-save-btn"
          onClick={save}
          className="h-9 bg-[#1A365D] px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
        >
          Save alias
        </button>
      </div>

      <div className="overflow-auto border border-slate-200 bg-white">
        <table className="data-table" data-testid="aliases-table">
          <thead>
            <tr>
              <th>Raw name</th>
              <th>Canonical name</th>
              <th>Entity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="py-6 text-center text-slate-500">
                  No {type} aliases yet. Names are used as-is until an alias is added.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.raw} data-testid={`alias-row-${r.raw}`}>
                <td className="max-w-[280px] truncate">{r.raw}</td>
                <td className="max-w-[320px] truncate font-medium">{r.canonical}</td>
                <td>{r.entity_type}</td>
                <td>
                  <button
                    data-testid={`delete-alias-${r.raw}`}
                    onClick={() => remove(r.raw)}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
